import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cpu, Wrench, ShieldAlert, Cog, Sliders, Truck, ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/data/site";

const ICONS = { Cpu, Wrench, ShieldAlert, Cog, Sliders, Truck };

const ICON_BY_ID = {
  "computer-diagnostics": "Cpu",
  "engines-overhaul": "Wrench",
  "suspensions-steering": "ShieldAlert",
  "clutches-drivetrain": "Cog",
  "transmissions-differentials": "Sliders",
  "247-towing-recovery": "Truck",
};

export default function Services() {
  const [active, setActive] = useState(SERVICES[0]);
  const ActiveIcon = ICONS[ICON_BY_ID[active.id]];

  return (
    <section id="services" className="relative py-24 lg:py-32 bg-carbon" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14"
        >
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-hazard font-semibold mb-3">/ 01 — Capabilities</p>
            <h2 className="font-heading uppercase font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">
              Full-Spectrum <span className="chrome-text">Diesel Service</span>
            </h2>
          </div>
          <p className="max-w-md text-slate-400 text-sm leading-relaxed">
            Select a system to inspect how we work it. Every bay job starts with diagnostics and ends with a verified road test.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 flex flex-col divide-y divide-steel border border-steel bg-surface">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[ICON_BY_ID[s.id]];
              const isActive = active.id === s.id;
              return (
                <motion.button
                  key={s.id}
                  data-testid={`service-card-${s.id}`}
                  onClick={() => setActive(s)}
                  initial={{ opacity: 0, x: -24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className={`group relative text-left px-6 py-5 flex items-center gap-4 transition-colors duration-300 ${
                    isActive ? "bg-card" : "hover:bg-card/60"
                  }`}
                >
                  <span className={`absolute left-0 top-0 bottom-0 w-1 transition-colors duration-300 ${isActive ? "bg-hazard" : "bg-transparent group-hover:bg-steel"}`} />
                  <span className={`font-mono text-xs font-bold ${isActive ? "text-hazard" : "text-slate-600"}`}>{s.num}</span>
                  <Icon className={`w-5 h-5 shrink-0 transition-colors duration-300 ${isActive ? "text-hazard" : "text-slate-500 group-hover:text-slate-300"}`} />
                  <span className={`font-heading font-bold uppercase text-lg tracking-wide flex-1 transition-colors duration-300 ${isActive ? "text-white" : "text-slate-400 group-hover:text-slate-200"}`}>
                    {s.title}
                  </span>
                  <ArrowUpRight className={`w-4 h-4 transition-all duration-300 ${isActive ? "text-hazard translate-x-0 opacity-100" : "text-slate-600 -translate-x-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-0"}`} />
                </motion.button>
              );
            })}
          </div>

          <div className="lg:col-span-7 relative min-h-[420px] border border-steel bg-surface overflow-hidden" data-testid="service-detail-panel">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0 flex flex-col"
              >
                <div className="relative h-56 sm:h-64 overflow-hidden">
                  <img src={active.image} alt={active.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/40 to-transparent" />
                  <span className="absolute top-4 right-4 font-heading font-black text-6xl text-white/10 select-none">{active.num}</span>
                  <span className="absolute bottom-4 left-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-hazard bg-carbon/80 backdrop-blur px-3 py-1.5 border border-steel">
                    <ActiveIcon className="w-3.5 h-3.5" /> Bay {active.num} / Active System
                  </span>
                </div>
                <div className="p-6 sm:p-8 flex-1 flex flex-col">
                  <h3 className="font-heading uppercase font-bold text-2xl sm:text-3xl text-white" data-testid="service-detail-title">{active.title}</h3>
                  <p className="mt-4 text-slate-300 leading-relaxed text-sm sm:text-base flex-1">{active.detail}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {active.tags.map((t) => (
                      <span key={t} className="px-3 py-1 border border-steel font-mono text-[10px] uppercase tracking-widest text-slate-400">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
